/**
 * Project Form Page
 *
 * Create or edit a project for cost tracking.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useProjectStore } from '../store/projectStore';
import { Card, Button, Input } from '../components/ui';
import type { CreateProjectRequest, UpdateProjectRequest } from '@budgira/shared';

// Predefined colors for projects
const PROJECT_COLORS = [
  '#8b5cf6', // violet-500
  '#06b6d4', // cyan-500
  '#f59e0b', // amber-500
  '#10b981', // emerald-500
  '#ef4444', // red-500
  '#ec4899', // pink-500
  '#3b82f6', // blue-500
  '#f97316', // orange-500
  '#14b8a6', // teal-500
  '#a855f7', // purple-500
  '#84cc16', // lime-500
  '#6366f1', // indigo-500
];

interface ProjectFormData {
  name: string;
  description: string;
  maxBudget: string;
  color: string;
}

const ProjectFormPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId?: string }>();
  const {
    projects,
    currentProject,
    isLoading,
    error,
    fetchProjects,
    fetchProject,
    createProject,
    updateProject,
    clearError,
    clearCurrentProject,
  } = useProjectStore();

  const isEditMode = projectId && projectId !== 'new';

  const [formData, setFormData] = useState<ProjectFormData>({
    name: '',
    description: '',
    maxBudget: '',
    color: PROJECT_COLORS[0] || '#8b5cf6',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Load projects list if needed
  useEffect(() => {
    if (projects.length === 0) {
      fetchProjects();
    }
  }, [projects.length, fetchProjects]);

  // Load project data in edit mode
  useEffect(() => {
    if (isEditMode && projectId) {
      fetchProject(projectId);
    }
    return () => {
      clearCurrentProject();
    };
  }, [isEditMode, projectId, fetchProject, clearCurrentProject]);

  // Populate form with existing project data
  useEffect(() => {
    if (isEditMode && currentProject) {
      setFormData({
        name: currentProject.name,
        description: currentProject.description || '',
        maxBudget: currentProject.maxBudget?.toString() || '',
        color: currentProject.color || PROJECT_COLORS[0] || '#8b5cf6',
      });
    }
  }, [isEditMode, currentProject]);

  const handleChange = (field: keyof ProjectFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormError(null);
  };

  const handleColorSelect = (color: string) => {
    setFormData((prev) => ({ ...prev, color }));
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setFormError(t('projects.form.errors.nameRequired'));
      return false;
    }

    if (formData.maxBudget && isNaN(parseFloat(formData.maxBudget))) {
      setFormError(t('projects.form.errors.invalidBudget'));
      return false;
    }

    if (formData.maxBudget && parseFloat(formData.maxBudget) < 0) {
      setFormError(t('projects.form.errors.negativeBudget'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      if (isEditMode && projectId) {
        // Update existing project
        const updateData: UpdateProjectRequest = {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          maxBudget: formData.maxBudget ? parseFloat(formData.maxBudget) : undefined,
          color: formData.color || undefined,
        };

        const updated = await updateProject(projectId, updateData);
        if (updated) {
          navigate('/projects');
        }
      } else {
        // Create new project
        const createData: CreateProjectRequest = {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          maxBudget: formData.maxBudget ? parseFloat(formData.maxBudget) : undefined,
          color: formData.color || undefined,
        };

        const created = await createProject(createData);
        if (created) {
          navigate('/projects');
        }
      }
    } catch (err) {
      console.error('Failed to save project:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/projects');
  };

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {isEditMode ? t('projects.form.titleEdit') : t('projects.form.titleNew')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
          {isEditMode ? t('projects.form.subtitleEdit') : t('projects.form.subtitleNew')}
        </p>
      </div>

      {/* Error display */}
      {(error || formError) && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-600 dark:text-red-400">{formError || error}</p>
          {error && (
            <button
              onClick={clearError}
              className="text-sm text-red-500 hover:text-red-700 dark:hover:text-red-300 mt-2"
            >
              {t('common.dismiss')}
            </button>
          )}
        </div>
      )}

      {/* Loading state for edit mode */}
      {isEditMode && isLoading && !currentProject ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" />
        </div>
      ) : (
        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Project Name */}
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                {t('projects.form.name')} *
              </label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder={t('projects.form.namePlaceholder')}
                required
                autoFocus
              />
            </div>

            {/* Description */}
            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                {t('projects.form.description')}
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder={t('projects.form.descriptionPlaceholder')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-violet-500 focus:border-transparent
                         placeholder-gray-400 dark:placeholder-gray-500"
              />
            </div>

            {/* Max Budget */}
            <div>
              <label
                htmlFor="maxBudget"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                {t('projects.form.maxBudget')}
              </label>
              <div className="relative">
                <Input
                  id="maxBudget"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.maxBudget}
                  onChange={(e) => handleChange('maxBudget', e.target.value)}
                  placeholder={t('projects.form.maxBudgetPlaceholder')}
                  className="pr-12"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
                  €
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('projects.form.maxBudgetHelp')}
              </p>
            </div>

            {/* Color Picker */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('projects.form.color')}
              </label>
              <div className="flex flex-wrap gap-2">
                {PROJECT_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => handleColorSelect(color)}
                    className={`w-8 h-8 rounded-full transition-transform hover:scale-110 ${
                      formData.color === color
                        ? 'ring-2 ring-offset-2 ring-gray-400 dark:ring-gray-500 dark:ring-offset-gray-800'
                        : ''
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
              {/* Custom color input */}
              <div className="mt-3 flex items-center gap-2">
                <label
                  htmlFor="customColor"
                  className="text-sm text-gray-500 dark:text-gray-400"
                >
                  {t('projects.form.customColor')}:
                </label>
                <input
                  id="customColor"
                  type="color"
                  value={formData.color}
                  onChange={(e) => handleColorSelect(e.target.value)}
                  className="w-10 h-8 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                />
                <span className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                  {formData.color}
                </span>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button type="button" variant="outline" onClick={handleCancel}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    {t('common.saving')}
                  </span>
                ) : isEditMode ? (
                  t('common.save')
                ) : (
                  t('projects.form.create')
                )}
              </Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
};

export default ProjectFormPage;
